import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { LogOut, MonitorSmartphone, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Spinner from '../../components/ui/Spinner';
import { formatDate } from '../../utils/format';
import { getErrorMessage } from '../../lib/apiClient';
import { useAuthStore } from '../../store/authStore';
import { listSessions, logoutEverywhere, revokeSession } from './authApi';

export default function SessionsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  // An admin "logging in as" someone must never sign the real user out of their devices.
  const isImpersonating = useAuthStore((s) => Boolean(s.impersonation));
  const [confirmAll, setConfirmAll] = useState(false);
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: listSessions,
  });

  const revokeMutation = useMutation({
    mutationFn: revokeSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      toast.success('Session revoked');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not revoke session')),
  });

  const logoutAllMutation = useMutation({
    mutationFn: logoutEverywhere,
    onSuccess: () => {
      // This device's session is gone too, so finish the sign-out locally.
      clear();
      queryClient.clear();
      toast.success('Signed out of all devices');
      navigate('/login', { replace: true });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not sign out of all devices')),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <MonitorSmartphone className="h-5 w-5 text-slate-500" />
        <h1 className="text-lg font-semibold text-slate-800">Active sessions</h1>
        {!isImpersonating && sessions.length > 0 && (
          <Button variant="danger" size="sm" className="ml-auto" onClick={() => setConfirmAll(true)}>
            <LogOut className="h-4 w-4" aria-hidden />
            Sign out of all devices
          </Button>
        )}
      </div>
      <p className="text-sm text-slate-500">
        Each device you sign in on holds a refresh session. Revoke any you don’t recognize.
      </p>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
        {isLoading ? (
          <div className="py-16">
            <Spinner />
          </div>
        ) : sessions.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">No active sessions.</div>
        ) : (
          sessions.map((s) => (
            <div key={s.id} className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-700">
                  {s.userAgent || 'Unknown device'}
                </p>
                <p className="text-xs text-slate-400">
                  {s.ip ? `${s.ip} · ` : ''}
                  {s.lastSeenAt ? `last seen ${formatDate(s.lastSeenAt)}` : `started ${formatDate(s.createdAt)}`}{' '}
                  · expires {formatDate(s.expiresAt)}
                </p>
              </div>
              <button
                title="Revoke session"
                onClick={() => revokeMutation.mutate(s.id)}
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        onConfirm={() => logoutAllMutation.mutate()}
        loading={logoutAllMutation.isPending}
        title="Sign out of all devices?"
        description="Every session is revoked, including this one. You will need to sign in again on each device."
        confirmLabel="Sign out everywhere"
      />
    </div>
  );
}
